/**
 * Back navigation for the installed (Home Screen) app.
 *
 * WHY THIS EXISTS: the manifest declares `display: standalone`, so iOS gives the app the whole
 * screen and NO browser chrome — which means no back button. Breadcrumbs are desktop-only by
 * deliberate decision (they became three truncated stubs at 375px), and the mobile drawer only
 * lists nav destinations. So once you opened a lead, a campaign or a conversation there was no way
 * back except the drawer, which returns you to a section root and loses your place. That is a
 * navigation dead-end, not a missing flourish.
 *
 * The hard part is knowing whether going back is SAFE. `navigate(-1)` on the first screen of a
 * standalone app walks out of the app entirely (to whatever preceded it, or a blank tab) — the
 * classic PWA back-button bug. So this tracks how deep the CURRENT session has pushed, and only
 * uses real history when we know we put something there; otherwise it navigates to the logical
 * parent. Deep-linking into a detail page therefore goes UP, never OUT.
 */
import * as React from 'react';
import { useLocation, useNavigate, useNavigationType, type NavigateFunction } from 'react-router-dom';

import { SYSTEM_NAV, WORKSPACE_NAV } from './nav';

/**
 * Every path reachable from the drawer, both contexts. Role is irrelevant here: a route you cannot
 * see is a route you cannot be standing on, and filtering by role would make the back control
 * appear or vanish based on permissions rather than on depth.
 */
const NAV_DESTINATIONS: ReadonlySet<string> = new Set(
  [...SYSTEM_NAV, ...WORKSPACE_NAV].flatMap((node) => node.items.map((item) => item.to)),
);

/**
 * The mobile navigation roots — the menu screens (see MenuPage). Under the Settings-style
 * drill-down these are the ONLY screens with nothing above them; a nav destination is now one level
 * down from its section list, not a root in its own right.
 */
const WORKSPACE_MENU = '/ws/menu';
const SYSTEM_MENU = '/menu';

/**
 * Which section (nav node) owns a destination path, so "up" from a page lands on its list.
 *
 * A section holding a SINGLE destination is skipped on the way down (MenuPage navigates straight to
 * the page rather than pushing a list of one), so it must be skipped on the way back too —
 * returning null sends "up" to the menu root instead. Otherwise back would land on a one-row screen
 * the user never saw going forward, which is the kind of asymmetry that makes a stack feel wrong
 * without being obviously broken.
 */
function sectionIdForDestination(path: string): { base: string; sectionId: string } | null {
  for (const [nav, base] of [
    [WORKSPACE_NAV, WORKSPACE_MENU],
    [SYSTEM_NAV, SYSTEM_MENU],
  ] as const) {
    for (const node of nav) {
      if (node.items.length < 2) continue; // single-destination section — not a screen
      if (node.items.some((item) => item.to === path)) return { base, sectionId: node.id };
    }
  }
  return null;
}

/** True for the two menu roots — nothing is above them, so no back affordance. */
export function isMenuRoot(pathname: string): boolean {
  const p = normalize(pathname);
  return p === WORKSPACE_MENU || p === SYSTEM_MENU;
}

/** Trailing slashes make `/ws/leads/` and `/ws/leads` different strings to a Set. */
function normalize(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

/** A nav destination is a place the drawer can reach — being there is not "deep". */
export function isNavDestination(pathname: string): boolean {
  return NAV_DESTINATIONS.has(normalize(pathname));
}

/**
 * Where "up" goes from `pathname` when there is no session history to pop.
 *
 * Prefers the longest nav destination that is a prefix of the current path (so
 * `/ws/campaigns/abc/steps` goes up to `/ws/campaigns`, not to the context root), and only falls
 * back to trimming a segment when the path lives outside the nav tree entirely. The prefix must end
 * at a segment boundary or `/ws/leads` would claim `/ws/leads-archive`.
 */
export function parentPathOf(pathname: string): string {
  const path = normalize(pathname);
  const inWorkspace = path === '/ws' || path.startsWith('/ws/');
  const root = inWorkspace ? WORKSPACE_MENU : SYSTEM_MENU;

  // A section list goes up to its menu root. Check the workspace prefix FIRST — `/ws/menu/x` also
  // contains "menu", and testing the system prefix first would send it to the wrong context.
  if (path.startsWith(`${WORKSPACE_MENU}/`)) return WORKSPACE_MENU;
  if (path.startsWith(`${SYSTEM_MENU}/`)) return SYSTEM_MENU;

  // A destination goes up to the list it appears in — the level that pushed it.
  const owner = sectionIdForDestination(path);
  if (owner) return `${owner.base}/${owner.sectionId}`;

  let best: string | null = null;
  for (const dest of NAV_DESTINATIONS) {
    if (dest === path) continue; // going up from a destination must not return itself
    if (!path.startsWith(dest)) continue;
    if (path[dest.length] !== '/') continue; // segment boundary, not a string prefix
    if (best === null || dest.length > best.length) best = dest;
  }
  if (best) return best;

  const cut = path.lastIndexOf('/');
  const trimmed = cut > 0 ? path.slice(0, cut) : '';
  // Only trust a trimmed path if it is itself a known destination; otherwise it is a guess that
  // could land on a route that does not exist and show the error boundary instead of going back.
  if (trimmed && isNavDestination(trimmed)) return trimmed;
  return root;
}

/**
 * In-app push depth, held at module scope rather than in a context.
 *
 * The browser history stack is a single global, so modelling it as one is honest — and it removes
 * the provider-placement trap: the layout that renders the back control is also the layout that
 * would have to host the provider, so a context would have to be read from above the component that
 * owns it. A module store has no such ordering.
 */
let navDepth = 0;
const depthListeners = new Set<() => void>();

/**
 * Direction of the most recent navigation, for the route transition. Module scope for the same
 * reason as the depth: it describes the one global history stack.
 */
export type NavDirection = 'forward' | 'back';
let lastDirection: NavDirection = 'forward';

export function getNavDirection(): NavDirection {
  return lastDirection;
}

/** True when the browser can snapshot the outgoing screen (Safari 18+, Chrome 111+). */
export function supportsViewTransitions(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document;
}

/**
 * Navigate with a real push/pop.
 *
 * The direction is written to `<html data-nav-dir>` BEFORE navigating, because the CSS that picks
 * the slide direction is evaluated when the transition starts — set it afterwards and the first
 * frame animates the wrong way. Without the attribute the browser cross-fades, which is the right
 * neutral fallback.
 *
 * `viewTransition` is passed to the router rather than calling `startViewTransition` here: the
 * snapshot has to wrap React's DOM update, and only the router knows when that happens.
 */
export function navigateWithPush(
  navigate: NavigateFunction,
  to: string | number,
  direction: NavDirection,
  opts: { replace?: boolean } = {},
): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.navDir = direction;
  }
  lastDirection = direction;
  if (typeof to === 'number') {
    // `navigate(-1)` takes no options; the attribute above still steers the CSS, and the router
    // runs its own transition for POPs when the browser supports them.
    navigate(to);
    return;
  }
  navigate(to, { ...opts, viewTransition: true });
}

function setNavDepth(next: number): void {
  if (next === navDepth) return;
  navDepth = next;
  for (const listener of depthListeners) listener();
}

function subscribeDepth(listener: () => void): () => void {
  depthListeners.add(listener);
  return () => depthListeners.delete(listener);
}

/** Test seam — a fresh cold start is depth 0. */
export function __resetNavDepthForTest(): void {
  setNavDepth(0);
}

/**
 * Counts in-app pushes so `goBack` knows whether `navigate(-1)` stays inside the app. Render once,
 * inside the router.
 *
 * Starts at 0 on every cold start, INCLUDING a deep link or a push-notification open — which is
 * exactly right: we did not push that entry, so we must not pop it.
 */
export function NavDepthTracker(): null {
  const navType = useNavigationType();
  const location = useLocation();

  React.useEffect(() => {
    if (navType === 'PUSH') {
      setNavDepth(navDepth + 1);
      lastDirection = 'forward';
    } else if (navType === 'POP') {
      setNavDepth(Math.max(0, navDepth - 1));
      lastDirection = 'back';
    }
    // REPLACE swaps the entry in place — the stack is no deeper than it was. Direction is left
    // alone: a replace is not a move through the hierarchy, and overwriting it here would make the
    // "go up from a deep link" case animate backwards on the way IN.

    // location.key changes on every navigation, including pushing the same path twice; pathname
    // alone would miss that and leave the depth short.
  }, [navType, location.key]);

  return null;
}

function useNavDepth(): number {
  return React.useSyncExternalStore(
    subscribeDepth,
    () => navDepth,
    () => 0,
  );
}

export interface BackTarget {
  /** True when the current route is deeper than a drawer destination. */
  canGoBack: boolean;
  goBack: () => void;
}

/**
 * The back affordance for the current route.
 *
 * Shown on any route that is not a drawer destination — that is the whole set of screens the drawer
 * cannot return you from, which is precisely where the dead-end was.
 */
export function useBackTarget(): BackTarget {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const depth = useNavDepth();

  // Under the drill-down model only the menu roots have nothing above them. A destination is one
  // level below its section list, so it DOES get a back — that is the whole point of the change.
  const canGoBack = !isMenuRoot(pathname);

  const goBack = React.useCallback(() => {
    if (depth > 0) {
      navigateWithPush(navigate, -1, 'back');
      return;
    }
    // Cold start on a deep route (deep link, push notification, refresh): there is nothing of ours
    // to pop, so walk UP the hierarchy instead of out of the app. Still animated as a pop — the
    // user is moving up, whatever the history stack happens to contain.
    navigateWithPush(navigate, parentPathOf(pathname), 'back', { replace: true });
  }, [depth, navigate, pathname]);

  return { canGoBack, goBack };
}
